// PlaylistDetailView — Vista detallada de un evento/playlist
// Extraído de App.js en Feb 2026 (Fase 2 del refactor — Context-based)
// Recibe todo el state y handlers via MainAppContext para evitar prop drilling.
//
// ⚠️ Este componente DEBE renderizarse dentro de <MainAppContext.Provider>.
import React from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import {
  Music, Plus, Edit2, Trash2, Search, Sparkles, Download,
  Upload, FileText, ExternalLink, Mail,
  MessageCircle, CheckCircle2,
  TrendingUp, Eye, EyeOff, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { EventOfflineControls } from "./EventOfflineControls";
import { BulkYouTubePrepareButton } from "./BulkYouTubePrepareButton";
import { EventSpacesDisplay } from "./EventSpaces";
import { EventScheduleTimelineBar, EventObservacionesList } from "./EventScheduleNotes";
import { EscaletaTab } from "./EscaletaTab";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { LiveProgressBar } from "./LiveProgressBar";
import { SortableSongList } from "./SortableSongList";
import { MomentosSortableView } from "./MomentosSortableView";
import { SortablePeticionItem } from "./SortablePeticionItem";
import LiveDashboard from "./LiveDashboard";
// O-02: lazy — GenreAnalysis carga `recharts`; solo se renderiza si el usuario
// activa "Análisis de Géneros" (showGenreAnalysis), así que no debe ir en el
// bundle eager de PlaylistDetailView.
const GenreAnalysis = React.lazy(() => import("./GenreAnalysis").then((m) => ({ default: m.GenreAnalysis })));
import { PlaylistEnergySparkline } from "./PlaylistEnergySparkline";
import EnergyCurve from "./EnergyCurve";
import BreakPlaylistChips from "./BreakPlaylistChips";
import FillQueueButton from "./FillQueueButton";
import ClientSpotifyPlaylists from "./ClientSpotifyPlaylists";
import { AudioUploaderDialog } from "./AudioUploaderDialog";
import { LiveMode2 } from "./LiveMode2";
import { MomentoSubTabs } from "./MomentoSubTabs";
import { SpotifyImportDialog } from "./SpotifyImportDialog";
import { AIReorderDialog } from "./AIReorderDialog";
import {
  guessBucketFromPeticion, BUCKET_LABELS,
} from "../utils/momentoBuckets";
import { NextTrackSuggester } from "./NextTrackSuggester";
import WhatsAppInfoCard from "./WhatsAppInfoCard";
import { EventFinances } from "./EventFinances";
import { ChecklistOnboarding } from "../ChecklistOnboarding";
import { GestionDocumentos } from "../GestionDocumentos";
import { GestionPresupuestos } from "../GestionPresupuestos";
import { GestionContratos } from "../GestionContratos";

import logger from "@/utils/logger";
import offlineDB from "../utils/offlineDB";
import { API_URL } from "../config";
import {
  MOMENTOS_BODA,
  normalizeText,
  getEffectiveCache,
  formatCurrency
} from "../constants";
import { useMainApp } from "../contexts/MainAppContext";
import { useAudioPlayback } from "../contexts/AudioPlaybackContext";
import { usePeticiones } from "../contexts/PeticionesContext";
import { useCustomList } from "../contexts/CustomListContext";
import { useNotes } from "../contexts/NotesContext";

const API = API_URL;

export default function PlaylistDetailView() {
  const ctx = useMainApp();
  const audioCtx = useAudioPlayback();
  const peticionesCtx = usePeticiones();
  const customListCtx = useCustomList();
  const notesCtx = useNotes();
  const navigate = useNavigate();
  const {
    playingAudioId,
    setPlayingAudioId,
    audioStates,
    setAudioStates,
    audioProgress,
    setAudioProgress,
    preloadAudio,
    cachedAudios,
  } = audioCtx;
  const {
    peticionNota,
    setPeticionNota,
    peticionesPendingFlags,
    setPeticionesPendingFlags,
    peticionesSearchQuery,
    setPeticionesSearchQuery,
    peticionesSearchResults,
    setPeticionesSearchResults,
    peticionesSearching,
    setPeticionesSearching,
    isPeticionesDialogOpen,
    setIsPeticionesDialogOpen,
    handlePeticionesDragEnd,
    handleRemovePeticion,
    handleTogglePeticionHidden,
  } = peticionesCtx;
  const {
    customListPlayedState,
    generatedCustomList,
    setGeneratedCustomList,
    filteredCustomList,
    setFilteredCustomList,
    listaPersonalizadaFilterEnergia,
    setListaPersonalizadaFilterEnergia,
    listaPersonalizadaFilterGenre,
    setListaPersonalizadaFilterGenre,
    listaPersonalizadaFilterMomento,
    setListaPersonalizadaFilterMomento,
    listaPersonalizadaSearch,
    setListaPersonalizadaSearch,
    listaPersonalizadaViewMode,
    setListaPersonalizadaViewMode,
    handleRemoveSongFromCustomList,
    handleReorderCustomList,
    handleReorderMomentosGlobal,
    handleResetCustomListPlayed,
    handleToggleCustomListPlayed,
    handleExportCustomListPDF,
  } = customListCtx;
  const {
    sharedNotes,
    setCurrentNote,
    setEditingNoteId,
    handleEditNote,
    handleDeleteNote,
    setIsNotesDialogOpen,
  } = notesCtx;
  const {
    accessToken,
    buscarEnBiblioteca,
    buscarEnSpotify,
    cacheStats,
    createSong,
    currentPlaylist,
    currentTab,
    enrichSongWithMarkers,
    fetchDenonPendientesCount,
    fetchPlaylistTemplates,
    fetchSmartPlaylistTemplates,
    getAllGenres,
    getAllMomentos,
    getCachedData,
    groupSongsByMomentos,
    handleAddSongToPlaylist,
    handleDeletePlaylist,
    handleExportToEngineDJ,
    handleExportToSpotify,
    handleGenerateAIRecommendationsForEvent,
    handleLongPressEditSong,
    handleTabChange,
    isAudioUploaderOpen,
    isGeneratingRecommendations,
    isLiveMode,
    openEditPlaylist,
    playlistSongs,
    queueOfflineAction,
    refetchPlaylists,
    setBuscarEnBiblioteca,
    setBuscarEnSpotify,
    setCachedData,
    setCurrentPlaylist,
    setIsAddToPlaylistOpen,
    setIsApplyTemplateOpen,
    setIsAudioUploaderOpen,
    setIsLiveMode,
    setIsMixChainsOpen,
    setIsMixSuggestionsOpen,
    setIsSaveTemplateOpen,
    setMixChainsMode,
    setMixSuggestionsSong,
    setSearchGenre,
    setSearchText,
    setSelectedSongsForChain,
    setShowClientTextImporter,
    setShowEventWizard,
    setShowGenreAnalysis,
    setShowWhatsAppImporter,
    setSpotifySearchCache,
    setTemplateForm,
    setView,
    showGenreAnalysis,
    songs,
    spotifyConnected,
    spotifySearchCache,
    token,
    user,
  } = ctx;

  // Reflejo "lista personalizada → playlist del cliente" (una dirección, solo
  // lectura): set de claves de las canciones marcadas como reproducidas en la
  // lista personalizada, por Spotify ID y por título+artista normalizado.
  // Se usa `playlistSongs` (canciones del custom list ya resueltas con todos sus
  // detalles); `songs` del contexto está paginado y no sirve como fuente.
  // ClientSpotifyPlaylists lo usa para marcar automáticamente esas mismas
  // canciones en la playlist del cliente ("↪ lista").
  const externalPlayedKeys = React.useMemo(() => {
    const set = new Set();
    const norm = (s) => (s || '').toString().toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ').trim();
    const byId = new Map((playlistSongs || []).map((s) => [s.id, s]));
    Object.entries(customListPlayedState || {}).forEach(([songId, st]) => {
      if (!st?.played) return;
      const song = byId.get(songId);
      if (!song) return;
      if (song.spotify_id) set.add('sid:' + song.spotify_id);
      if (song.title) set.add('ta:' + norm(song.title) + '|' + norm(song.artist));
    });
    return set;
  }, [customListPlayedState, playlistSongs]);

  // Confirmación de "Vaciar playlist" — AlertDialog (más fiable y consistente en
  // iOS PWA que el window.confirm nativo). Acción destructiva sin vuelta atrás.
  const [clearPlaylistConfirmOpen, setClearPlaylistConfirmOpen] = React.useState(false);

  // Toggle "Ocultar reproducidas": oculta de la vista las canciones marcadas como
  // reproducidas (los datos no se tocan). Útil en directo para ver solo lo que falta.
  const [hidePlayedSongs, setHidePlayedSongs] = React.useState(false);

  // ── SUBPESTAÑAS POR MOMENTO (Feb 2026) ────────────────────────────────
  // La pestaña "Peticiones" se subdivide en 4 buckets: ceremonia / coctel /
  // banquete / baile. El bucket activo filtra tanto el listado plano
  // (no-live) como el pad (live). Overrides de visibilidad se persisten en
  // `currentPlaylist.momento_tab_overrides` — permite forzar mostrar/ocultar
  // subpestañas por evento (ej: ocultar Cóctel en bodas sin cóctel).
  const [activeMomentoBucket, setActiveMomentoBucket] = React.useState('baile');
  const momentoOverrides = React.useMemo(
    () => currentPlaylist?.momento_tab_overrides || {},
    [currentPlaylist?.momento_tab_overrides]
  );
  const handleMomentoOverridesChange = React.useCallback(async (nextOverrides) => {
    if (!currentPlaylist?.id) return;
    // Optimista: actualiza local
    setCurrentPlaylist({ ...currentPlaylist, momento_tab_overrides: nextOverrides });
    try {
      const t = localStorage.getItem('token');
      await axios.put(
        `${API}/playlists/${currentPlaylist.id}/momento-tab-overrides`,
        { overrides: nextOverrides },
        { headers: { Authorization: `Bearer ${t}` } },
      );
    } catch (e) {
      console.error('Error guardando overrides:', e);
      toast.error('No se pudo guardar la visibilidad de subpestañas');
    }
  }, [currentPlaylist, setCurrentPlaylist]);
  const [spotifyImportOpen, setSpotifyImportOpen] = React.useState(false);
  const [aiReorderOpen, setAiReorderOpen] = React.useState(false);

  // Filtro de peticiones por bucket activo (para la vista NO-live)
  const filteredPeticionesByBucket = React.useMemo(() => {
    const list = currentPlaylist?.peticiones || [];
    return list
      .map((p, originalIndex) => ({ p, originalIndex }))
      .filter(({ p }) => guessBucketFromPeticion(p) === activeMomentoBucket);
  }, [currentPlaylist?.peticiones, activeMomentoBucket]);

  const handleClearPlaylistConfirmed = async () => {
    setClearPlaylistConfirmOpen(false);
    try {
      const res = await axios.delete(`${API}/event-wizard/clear-playlist/${currentPlaylist.id}`);
      toast.success(res.data.message || 'Playlist vaciada');
      const refreshed = await axios.get(`${API}/playlists/${currentPlaylist.id}`);
      setCurrentPlaylist(refreshed.data);
      setGeneratedCustomList([]);
      setFilteredCustomList([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error vaciando playlist');
    }
  };

  return (
    <>
            {/* Botón flotante "Sugerir siguiente" (Next Track Suggester IA) — solo en Modo En Vivo */}
            {isLiveMode && currentPlaylist?.id && (
              <NextTrackSuggester
                eventId={currentPlaylist.id}
                currentSongId={null}
                token={token}
                visible={true}
              />
            )}
            {/* Barra de progreso del Modo En Vivo (feature Feb 2026) */}
            {isLiveMode && (
              <LiveProgressBar
                songs={generatedCustomList}
                playedState={customListPlayedState}
              />
            )}
            <div className="mb-4">
              <Button
                onClick={() => {
                  // Push (no replace) → añade entrada al historial. Así el
                  // botón "adelante" del navegador vuelve al detalle si el DJ
                  // se arrepiente. La limpieza de state la hace el hook
                  // `useUrlEventSync` al detectar URL sin params relevantes
                  // + currentPlaylist activo.
                  navigate('/');
                }}
                variant="outline"
                className="mb-3 bg-slate-700 text-white"
                data-testid="btn-volver-a-playlists"
              >
                ← Volver a Playlists
              </Button>

              <Card className="bg-slate-800 border-slate-700 mb-4">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                    <h2 className="text-xl sm:text-2xl font-bold text-white">{currentPlaylist.nombre_novios}</h2>
                    <div className="flex flex-wrap gap-2">
                      {user?.role === 'admin' && !isLiveMode && (
                        <Button
                          onClick={async () => {
                            try {
                              let code = currentPlaylist.client_access_code;
                              
                              // Si no tiene código, generar uno nuevo
                              if (!code) {
                                const response = await axios.post(
                                  `${API}/playlists/client/generate-code/${currentPlaylist.id}`,
                                  {},
                                  { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
                                );
                                code = response.data.codigo_acceso || response.data.access_code;
                                
                                // Actualizar el playlist actual
                                setCurrentPlaylist({
                                  ...currentPlaylist,
                                  client_access_code: code
                                });
                              }
                              
                              const portalUrl = `${window.location.origin}/portal?code=${code}`;
                              const nombreEvento = currentPlaylist.nombre_novios || 'tu evento';
                              const fechaEvento = currentPlaylist.fecha_evento 
                                ? new Date(currentPlaylist.fecha_evento).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })
                                : '';
                              
                              const mensaje = `¡Hola! 👋

Te envío el acceso a tu *Portal de Cliente* para ${nombreEvento}${fechaEvento ? ` (${fechaEvento})` : ''}.

📱 *Accede directamente aquí:*
${portalUrl}

Desde el portal puedes:
✅ Ver información del evento
🎵 Añadir tus preferencias musicales
📄 Ver presupuestos y contratos
📤 Subir documentos

¡Cualquier duda me dices!`;

                              // Si hay teléfono, abrir WhatsApp directamente
                              const telefono = currentPlaylist.telefono_novios;
                              if (telefono) {
                                const telefonoLimpio = telefono.replace(/\D/g, '');
                                const telefonoConPrefijo = telefonoLimpio.startsWith('34') ? telefonoLimpio : `34${telefonoLimpio}`;
                                const whatsappUrl = `https://wa.me/${telefonoConPrefijo}?text=${encodeURIComponent(mensaje)}`;
                                window.open(whatsappUrl, '_blank');
                                toast.success('Abriendo WhatsApp...');
                              } else {
                                // Si no hay teléfono, copiar al portapapeles
                                try {
                                  await navigator.clipboard.writeText(mensaje);
                                  toast.success('Enlace copiado. No hay teléfono registrado para WhatsApp.', {
                                    description: 'Añade el teléfono del cliente en los datos del evento.'
                                  });
                                } catch (err) {
                                  toast.info(
                                    <div className="space-y-1">
                                      <p className="font-semibold">Link de acceso:</p>
                                      <p className="text-xs bg-slate-700 p-2 rounded break-all">{portalUrl}</p>
                                    </div>,
                                    { duration: 8000 }
                                  );
                                }
                              }
                            } catch (error) {
                              console.error('Error:', error);
                              toast.error('Error al generar código');
                            }
                          }}
                          variant="outline"
                          size="sm"
                          className="bg-green-700 text-white hover:bg-green-600 text-xs sm:text-sm"
                        >
                          <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Portal Cliente</span>
                          <span className="sm:hidden">Portal</span>
                        </Button>
                      )}
                      {user?.role === 'admin' && !isLiveMode && (() => {
                        const f = currentPlaylist?.fecha_evento;
                        const fd = f ? new Date(f) : null;
                        const eventPast = (currentPlaylist?.estado || '').toLowerCase() === 'completado' ||
                          (fd && !isNaN(fd.getTime()) && fd < new Date(new Date().toDateString()));
                        if (!eventPast) return null;
                        return (
                          <Button
                            onClick={async () => {
                              try {
                                const res = await axios.get(`${API}/recuerdo/admin/${currentPlaylist.id}`, {
                                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                                  responseType: 'blob',
                                });
                                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                window.open(url, '_blank');
                                toast.success('Recuerdo generado 💛');
                              } catch (err) {
                                if (err?.response?.status === 425) {
                                  toast.error('El recuerdo estará disponible después del evento.');
                                } else {
                                  toast.error('Error al generar el recuerdo');
                                }
                              }
                            }}
                            variant="outline"
                            size="sm"
                            className="bg-rose-700 text-white hover:bg-rose-600 text-xs sm:text-sm"
                            data-testid="recuerdo-pdf-btn"
                          >
                            <Download className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                            <span className="hidden sm:inline">Recuerdo</span>
                            <span className="sm:hidden">PDF</span>
                          </Button>
                        );
                      })()}
                      {user?.role === 'admin' && !isLiveMode && (
                        <Button
                          onClick={async () => {
                            try {
                              toast.info('📄 Generando DJ Brief...');
                              const res = await axios.get(`${API}/playlists/${currentPlaylist.id}/dj-brief-pdf`, {
                                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                                responseType: 'blob',
                              });
                              const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                              const link = document.createElement('a');
                              link.href = url;
                              const safe = (currentPlaylist.nombre_novios || 'evento').replace(/\s+/g, '_');
                              link.setAttribute('download', `dj_brief_${safe}.pdf`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                              toast.success('✅ DJ Brief descargado');
                            } catch (err) {
                              console.error('Error DJ Brief:', err);
                              toast.error('Error al generar el DJ Brief');
                            }
                          }}
                          variant="outline"
                          size="sm"
                          className="bg-blue-800 text-white hover:bg-blue-700 text-xs sm:text-sm"
                          data-testid="dj-brief-pdf-btn"
                        >
                          <FileText className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                          <span className="hidden sm:inline">DJ Brief</span>
                          <span className="sm:hidden">Brief</span>
                        </Button>
                      )}

                      <Button
                        onClick={() => {
                          setIsLiveMode(!isLiveMode);
                          toast.success(isLiveMode ? '✓ Modo Normal' : '🔴 Modo En Vivo Activado');
                        }}
                        size="sm"
                        className={`${isLiveMode ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-purple-600 hover:bg-purple-700'} text-white text-xs sm:text-sm`}
                        data-testid="live-mode-toggle-btn"
                        aria-pressed={isLiveMode}
                      >
                        {isLiveMode ? '🔴' : '▶️'} <span className="hidden sm:inline">{isLiveMode ? 'MODO VIVO' : 'Modo En Vivo'}</span><span className="sm:hidden">{isLiveMode ? 'VIVO' : 'En Vivo'}</span>
                      </Button>
                      {user?.role === 'admin' && !isLiveMode && (
                        <Button
                          onClick={() => openEditPlaylist(currentPlaylist)}
                          variant="outline"
                          size="sm"
                          className="bg-slate-700 text-cyan-400 text-xs sm:text-sm"
                        >
                          <Edit2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Editar</span>
                          <span className="sm:hidden">Edit</span>
                        </Button>
                      )}
                      {user?.role === 'admin' && !isLiveMode && (
                        <Button
                          onClick={() => setShowWhatsAppImporter(true)}
                          variant="outline"
                          size="sm"
                          className="bg-green-800 text-green-300 hover:bg-green-700 text-xs sm:text-sm"
                        >
                          <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                          <span className="hidden sm:inline">WhatsApp</span>
                          <span className="sm:hidden">WA</span>
                        </Button>
                      )}
                      {user?.role === 'admin' && !isLiveMode && (
                        <Button
                          onClick={() => setShowClientTextImporter(true)}
                          variant="outline"
                          size="sm"
                          className="bg-emerald-800 text-emerald-200 hover:bg-emerald-700 text-xs sm:text-sm"
                          data-testid="import-client-email-btn"
                        >
                          <Mail className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Email cliente</span>
                          <span className="sm:hidden">Email</span>
                        </Button>
                      )}
                      {user?.role === 'admin' && !isLiveMode && currentPlaylist && (
                        <Button
                          onClick={() => handleDeletePlaylist(currentPlaylist.id)}
                          variant="outline"
                          size="sm"
                          className="bg-red-900/50 text-red-400 hover:bg-red-800 border-red-700 text-xs sm:text-sm"
                          data-testid="delete-event-detail-btn"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Eliminar</span>
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Spotify Copilot — Playlists salvavidas + Fill Queue (1-tap) */}
                  <div className="mb-3 flex items-center gap-3 flex-wrap">
                    <BreakPlaylistChips />
                    <FillQueueButton playlistId={currentPlaylist.id} />
                  </div>

                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-2 text-sm mb-3 ${isLiveMode ? 'hidden' : ''}`}>
                    {currentPlaylist.fecha_evento && (
                      <p className="text-slate-300">📅 <strong>Fecha:</strong> {new Date(currentPlaylist.fecha_evento).toLocaleDateString('es-ES')}</p>
                    )}
                    {currentPlaylist.lugar_evento && (
                      <p className="text-slate-300">📍 <strong>Lugar:</strong> {currentPlaylist.lugar_evento}</p>
                    )}
                    {currentPlaylist.horario_inicio && (
                      <p className="text-slate-300">🕐 <strong>Inicio:</strong> {currentPlaylist.horario_inicio}</p>
                    )}
                    {currentPlaylist.horario_fin && (
                      <p className="text-slate-300">🕐 <strong>Fin:</strong> {currentPlaylist.horario_fin}</p>
                    )}
                    {currentPlaylist.numero_invitados && (
                      <p className="text-slate-300">👥 <strong>Invitados:</strong> {currentPlaylist.numero_invitados}</p>
                    )}
                    {currentPlaylist.montaje_elegido && (
                      <p className="text-slate-300">🎪 <strong>Montaje:</strong> {currentPlaylist.montaje_elegido}</p>
                    )}
                    {currentPlaylist.contacto_novios && (
                      <p className="text-slate-300">📞 <strong>Contacto:</strong> {currentPlaylist.contacto_novios}</p>
                    )}
                    {user?.role === 'admin' && currentPlaylist.telefono_novios && (
                      <p className="text-slate-300">
                        📱 <strong>Teléfono:</strong>{' '}
                        <a 
                          href={`https://wa.me/34${currentPlaylist.telefono_novios.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-400 hover:text-green-300 hover:underline"
                        >
                          {currentPlaylist.telefono_novios}
                        </a>
                      </p>
                    )}
                    {user?.role === 'admin' && currentPlaylist.email_novios && (
                      <p className="text-slate-300">
                        ✉️ <strong>Email:</strong>{' '}
                        <a 
                          href={`mailto:${currentPlaylist.email_novios}`}
                          className="text-cyan-400 hover:text-cyan-300 hover:underline"
                        >
                          {currentPlaylist.email_novios}
                        </a>
                      </p>
                    )}
                    {currentPlaylist.nombre_dj && (
                      <p className="text-purple-300">🎧 <strong>DJ:</strong> {currentPlaylist.nombre_dj}</p>
                    )}
                    {currentPlaylist.nombre_asistente && (
                      <p className="text-teal-300">👤 <strong>Asistente:</strong> {currentPlaylist.nombre_asistente}</p>
                    )}
                    {currentPlaylist.nombre_asistente2 && (
                      <p className="text-teal-300">👤 <strong>Asistente 2:</strong> {currentPlaylist.nombre_asistente2}</p>
                    )}
                    {currentPlaylist.estado && (
                      <p className="text-slate-300">
                        📊 <strong>Estado:</strong>{' '}
                        <span className={`px-2 py-1 rounded text-xs ${
                          currentPlaylist.estado === 'completado' ? 'bg-green-600/30 text-green-300' :
                          currentPlaylist.estado === 'confirmado' ? 'bg-blue-600/30 text-blue-300' :
                          'bg-yellow-600/30 text-yellow-300'
                        }`}>
                          {currentPlaylist.estado.charAt(0).toUpperCase() + currentPlaylist.estado.slice(1)}
                        </span>
                      </p>
                    )}
                    {/* Importe del Evento - Calculado desde caché, presupuesto o servicios */}
                    {user?.role === 'admin' && getEffectiveCache(currentPlaylist) > 0 && (
                      <p className="text-emerald-300">
                        💰 <strong>Importe:</strong>{' '}
                        <span className="font-semibold">{formatCurrency(getEffectiveCache(currentPlaylist))}</span>
                      </p>
                    )}
                    {user?.role === 'admin' && currentPlaylist.fianza !== undefined && (
                      <p className="text-slate-300">
                        💰 <strong>Fianza:</strong>{' '}
                        <span className={`px-2 py-1 rounded text-xs ${
                          currentPlaylist.fianza ? 'bg-green-600/30 text-green-300' : 'bg-red-600/30 text-red-300'
                        }`}>
                          {currentPlaylist.fianza ? 'Pagada ✓' : 'Pendiente'}
                        </span>
                      </p>
                    )}
                  </div>

                  {/* (2026-10, Fase 2) Espacios y horarios del venue — marco general (5 momentos)
                      + detalle fino (línea de tiempo de espacios) en una misma vista */}
                  {!isLiveMode && (
                    (currentPlaylist.espacios_evento && currentPlaylist.espacios_evento.length > 0) ||
                    (currentPlaylist.escaleta && currentPlaylist.escaleta.some((b) => b && b.espacio && b.hora)) ||
                    (currentPlaylist.horarios_momentos && currentPlaylist.horarios_momentos.length > 0)
                  ) && (
                    <div className="mb-3" data-testid="event-detail-spaces">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <p className="text-slate-400 text-xs"><strong>📍 Espacios y horarios del venue:</strong></p>
                        {currentPlaylist.espacios_sugeridos_ia && (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-300 bg-amber-500/15 border border-amber-500/40 rounded-full px-2 py-0.5"
                            data-testid="event-spaces-ai-badge"
                            title="Detectados automáticamente por IA al importar el evento. Edita el evento para confirmarlos."
                          >
                            <Sparkles className="w-3 h-3" /> Sugeridos por IA — revisar
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        {currentPlaylist.horarios_momentos && currentPlaylist.horarios_momentos.length > 0 && (
                          <div>
                            <p className="text-[11px] text-slate-500 mb-1">Marco general (5 momentos)</p>
                            <EventScheduleTimelineBar horarios={currentPlaylist.horarios_momentos} />
                          </div>
                        )}
                        {(
                          (currentPlaylist.espacios_evento && currentPlaylist.espacios_evento.length > 0) ||
                          (currentPlaylist.escaleta && currentPlaylist.escaleta.some((b) => b && b.espacio && b.hora))
                        ) && (
                          <div>
                            {currentPlaylist.horarios_momentos && currentPlaylist.horarios_momentos.length > 0 && (
                              <p className="text-[11px] text-slate-500 mb-1 mt-2">Detalle por espacio (Escaleta)</p>
                            )}
                            <EventSpacesDisplay espacios={currentPlaylist.espacios_evento} escaleta={currentPlaylist.escaleta} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Observaciones operativas (2026-10, Fase 1) — checklist de montaje interactivo (Fase 3) */}
                  {!isLiveMode && currentPlaylist.observaciones_operativas && currentPlaylist.observaciones_operativas.length > 0 && (
                    <div className="mb-4" data-testid="event-schedule-notes-section">
                      <Label className="flex items-center gap-2 text-amber-300 mb-2">
                        <Clock className="w-4 h-4" /> Observaciones operativas del venue
                      </Label>
                      <EventObservacionesList
                        observaciones={currentPlaylist.observaciones_operativas}
                        interactive
                        onToggleCompletado={async (obsId, completado) => {
                          const prevObservaciones = currentPlaylist.observaciones_operativas;
                          setCurrentPlaylist({
                            ...currentPlaylist,
                            observaciones_operativas: prevObservaciones.map((o) =>
                              o.id === obsId ? { ...o, completado } : o
                            ),
                          });
                          try {
                            const token = localStorage.getItem('token');
                            await axios.patch(
                              `${API}/playlists/${currentPlaylist.id}/observaciones/${obsId}/toggle`,
                              { completado },
                              { headers: { Authorization: `Bearer ${token}` } },
                            );
                          } catch (e) {
                            // Revertir el estado optimista si el guardado falla en el servidor
                            setCurrentPlaylist((prev) => ({
                              ...prev,
                              observaciones_operativas: prevObservaciones,
                            }));
                            toast.error('No se pudo guardar el checklist');
                          }
                        }}
                      />
                    </div>
                  )}

                  
                  {/* Playlists de Spotify - Mostrar todas con etiquetas */}
                  {!isLiveMode && ((currentPlaylist.playlist_spotify_urls && currentPlaylist.playlist_spotify_urls.length > 0) || currentPlaylist.playlist_spotify) && (
                    <div className="mb-3">
                      <p className="text-slate-400 text-xs mb-2"><strong>🎵 Playlists de Spotify del Cliente:</strong></p>
                      <div className="space-y-2">
                        {/* Nuevas playlists con etiquetas */}
                        {currentPlaylist.playlist_spotify_urls && currentPlaylist.playlist_spotify_urls.length > 0 ? (
                          currentPlaylist.playlist_spotify_urls.map((item, index) => (
                            <div key={item.id || index} className="flex items-center gap-2 bg-slate-900 p-2 rounded-lg border border-slate-700">
                              {item.label && (
                                <span className="bg-cyan-600/30 text-cyan-300 px-2 py-1 rounded text-xs font-semibold whitespace-nowrap">
                                  {item.label}
                                </span>
                              )}
                              <a 
                                href={item.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm transition-colors flex-1"
                              >
                                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                                </svg>
                                <span className="truncate">Abrir en Spotify</span>
                              </a>
                            </div>
                          ))
                        ) : (
                          /* Playlist legacy (para compatibilidad con eventos antiguos) */
                          currentPlaylist.playlist_spotify && (
                            <a 
                              href={currentPlaylist.playlist_spotify} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm transition-colors"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                              </svg>
                              Abrir en Spotify
                            </a>
                          )
                        )}
                      </div>
                    </div>
                  )}
                  
                  {!isLiveMode && currentPlaylist.servicios_contratados && currentPlaylist.servicios_contratados.length > 0 && (
                    <div className="mb-3">
                      <p className="text-slate-400 text-xs mb-1"><strong>Servicios Contratados:</strong></p>
                      <div className="flex flex-wrap gap-1">
                        {currentPlaylist.servicios_contratados.map(servicio => (
                          <span key={servicio.servicio} className="bg-purple-600/30 text-purple-300 px-2 py-1 rounded text-xs">
                            {servicio.servicio}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-cyan-400 mb-2">{playlistSongs.length} {playlistSongs.length === 1 ? 'canción' : 'canciones'}</p>
                  
                  {/* Información extraída de WhatsApp */}
                  {!isLiveMode && (
                    <WhatsAppInfoCard 
                      event={currentPlaylist} 
                      onAddMore={() => setShowWhatsAppImporter(true)} 
                    />
                  )}
                  
                  {currentPlaylist.notas && (
                    <p className="text-slate-400 text-sm italic border-t border-slate-700 pt-2 whitespace-pre-line">{currentPlaylist.notas}</p>
                  )}

                  {/* Momentos especiales estructurados (de Texto/WhatsApp/Documentos del DJ Master) */}
                  {currentPlaylist.momentos_especiales_evento?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-700" data-testid="momentos-especiales-evento-list">
                      <p className="text-amber-400 text-xs font-semibold mb-2">✨ Momentos especiales del evento</p>
                      <div className="space-y-2">
                        {currentPlaylist.momentos_especiales_evento.map((m) => (
                          <div key={m.id} className="bg-slate-900/50 border border-amber-700/30 rounded p-2" data-testid={`momento-especial-${m.id}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              {m.tipo && <span className="bg-amber-700/60 text-amber-100 px-1.5 py-0.5 rounded text-[10px] shrink-0">{m.tipo}</span>}
                              <span className="text-white text-sm font-medium">{m.titulo}</span>
                              {m.cuando && <span className="text-slate-500 text-xs">⏰ {m.cuando}</span>}
                            </div>
                            {m.descripcion && <p className="text-slate-300 text-xs mt-1">{m.descripcion}</p>}
                            {m.participantes && <p className="text-slate-500 text-xs mt-0.5">👥 {m.participantes}</p>}
                            {m.frase_dj && <p className="text-emerald-300 text-xs italic mt-0.5">🎤 "{m.frase_dj}"</p>}
                            {m.canciones?.length > 0 && (
                              <p className="text-slate-400 text-xs mt-0.5">
                                ♪ {m.canciones.map((c) => `${c.titulo}${c.artista ? ` - ${c.artista}` : ''}`).join(', ')}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tabs para organizar contenido del evento */}
              <div className="mb-6">
                <div className="border-b border-slate-700">
                  <div className="flex gap-1 overflow-x-auto">
                    {[
                      { id: 'lista', label: 'Lista Personalizada', icon: '🎵' },
                      { id: 'playlists-cliente', label: 'Playlists Cliente', icon: '🎧' },
                      { id: 'peticiones', label: 'Peticiones', icon: '🎤' },
                      { id: 'peticiones-vivo', label: 'Peticiones en Vivo', icon: '⚡' },
                      ...(isLiveMode ? [] : [
                        // Finanzas solo para admin
                        ...(user?.role === 'admin' ? [{ id: 'finanzas', label: 'Finanzas', icon: '💰' }] : []),
                        { id: 'escaleta', label: 'Escaleta', icon: '📋' },
                        { id: 'notas', label: 'Notas', icon: '📝' },
                        { id: 'checklist', label: 'Checklist', icon: '✅' },
                        { id: 'documentos', label: 'Documentos', icon: '📄' }
                      ]),
                      ...(user?.role === 'admin' && !isLiveMode ? [
                        { id: 'presupuestos', label: 'Presupuestos', icon: '💰' },
                        { id: 'contratos', label: 'Contratos', icon: '📝' }
                      ] : [])
                    ].map(tab => (
                      <button
                        key={tab.id}
                        data-testid={`pl-tab-${tab.id}`}
                        onClick={() => handleTabChange(tab.id)}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                          currentTab === tab.id
                            ? 'border-sky-500 text-sky-400'
                            : 'border-transparent text-slate-400 hover:text-slate-300'
                        }`}
                      >
                        {tab.icon} {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Tab: Lista Personalizada */}
              {currentTab === 'lista' && (
                <div className="space-y-4">
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        🎵 Lista Personalizada
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Mostrar criterios del evento */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-900/50 rounded-lg">
                        <div>
                          <p className="text-slate-400 text-sm mb-1">Gustos Musicales:</p>
                          <p className="text-white">{currentPlaylist.gustos_musicales || 'No especificados'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-sm mb-1">Canciones Prohibidas:</p>
                          <p className="text-white">{currentPlaylist.canciones_prohibidas || 'Ninguna'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-sm mb-1">Canciones Imperdibles:</p>
                          <p className="text-white">{currentPlaylist.canciones_imperdibles || 'Ninguna'}</p>
                        </div>
                        {currentPlaylist.hora_loca_cancion && (
                          <div>
                            <p className="text-slate-400 text-sm mb-1">Canción de Hora Loca:</p>
                            <p className="text-white">{currentPlaylist.hora_loca_cancion}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-slate-400 text-sm mb-1">Duración del Baile:</p>
                          <p className="text-white">{currentPlaylist.duracion_baile || 'No especificada'}</p>
                        </div>
                      </div>

                      {/* Botón Wizard - Preparar Evento */}
                      <Button
                        onClick={() => setShowEventWizard(true)}
                        className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white mb-2"
                        data-testid="prepare-event-wizard-btn"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Preparar Evento (Wizard IA)
                      </Button>

                      {/* Atajo: aplicar una plantilla existente directamente, sin
                          pasar por el Wizard (solo visible cuando aún no hay lista). */}
                      {!isLiveMode && (!currentPlaylist.lista_personalizada_generada || currentPlaylist.lista_personalizada_generada.length === 0) && (
                        <Button
                          onClick={() => {
                            fetchPlaylistTemplates();
                            fetchSmartPlaylistTemplates();
                            setIsApplyTemplateOpen(true);
                          }}
                          variant="outline"
                          className="w-full border-green-500/50 bg-green-950/30 text-green-300 hover:bg-green-900/50 hover:text-green-200 mb-2"
                          data-testid="apply-template-empty-btn"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Aplicar plantilla existente
                        </Button>
                      )}
                      
                      {/* Iter89.16: Botón Vaciar playlist (Iter114: confirmación AlertDialog) */}
                      {currentPlaylist.lista_personalizada_generada && currentPlaylist.lista_personalizada_generada.length >= 4 && (
                        <Button
                          onClick={() => setAiReorderOpen(true)}
                          variant="outline"
                          className="w-full border-cyan-500/60 bg-cyan-950/30 text-cyan-200 hover:bg-cyan-900/50 hover:text-cyan-100 mb-2"
                          data-testid="ai-reorder-open-btn"
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          Ordenar con IA por patrones
                        </Button>
                      )}
                      {currentPlaylist.lista_personalizada_generada && currentPlaylist.lista_personalizada_generada.length > 0 && (
                        <Button
                          onClick={() => setClearPlaylistConfirmOpen(true)}
                          variant="outline"
                          size="sm"
                          className="w-full border-rose-500/50 bg-rose-950/30 text-rose-300 hover:bg-rose-900/50 hover:text-rose-200 mb-3"
                          data-testid="clear-playlist-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Vaciar playlist del evento ({currentPlaylist.lista_personalizada_generada.length} canciones)
                        </Button>
                      )}

                      {/* Confirmación de vaciado de playlist */}
                      <AlertDialog open={clearPlaylistConfirmOpen} onOpenChange={setClearPlaylistConfirmOpen}>
                        <AlertDialogContent className="bg-slate-800 border-slate-700 text-white" data-testid="clear-playlist-confirm-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">¿Vaciar la playlist del evento?</AlertDialogTitle>
                            <AlertDialogDescription className="text-slate-400">
                              Se eliminarán las <span className="text-white font-semibold">{currentPlaylist.lista_personalizada_generada?.length || 0} canciones</span> de la lista personalizada. Las peticiones, la configuración del wizard y tu biblioteca permanecen intactas. Esta acción no se puede deshacer.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel
                              className="bg-slate-700 text-white border-slate-600 hover:bg-slate-600 hover:text-white"
                              data-testid="clear-playlist-cancel"
                            >
                              Cancelar
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleClearPlaylistConfirmed}
                              className="bg-rose-600 hover:bg-rose-700 text-white"
                              data-testid="clear-playlist-confirm"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Vaciar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>

                      {/* Mostrar lista generada */}
                      {currentPlaylist.lista_personalizada_generada && currentPlaylist.lista_personalizada_generada.length > 0 && (
                        <>
                        <div className="space-y-3">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <h3 className="text-white font-semibold">
                              Lista Generada ({currentPlaylist.lista_personalizada_generada.length} canciones)
                            </h3>
                            <div className="flex gap-2 flex-wrap">
                              <Button
                                onClick={() => {
                                  // Pre-rellena la búsqueda del diálogo con lo que el
                                  // DJ acaba de buscar en la lista: si no estaba en la
                                  // lista, lo busca directamente en toda la biblioteca
                                  // sin teclear de nuevo.
                                  setSearchText(listaPersonalizadaSearch || '');
                                  setSearchGenre('');
                                  setIsAddToPlaylistOpen(true);
                                }}
                                size="sm"
                                className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs"
                                data-testid="open-add-song-dialog"
                              >
                                <Plus className="w-4 h-4 sm:mr-2" />
                                <span className="hidden sm:inline">Añadir</span>
                              </Button>
                              <Button
                                onClick={() => setShowGenreAnalysis(!showGenreAnalysis)}
                                size="sm"
                                className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                              >
                                <TrendingUp className="w-4 h-4 sm:mr-2" />
                                <span className="hidden sm:inline">{showGenreAnalysis ? 'Ocultar' : 'Análisis'}</span>
                              </Button>
                              {/* Ocultar botones de exportación en modo En Vivo para ganar espacio */}
                              {!isLiveMode && (
                                <>
                                  <Button
                                    onClick={() => {
                                      setTemplateForm({
                                        nombre: `${currentPlaylist.nombre_novios} - Lista`,
                                        descripcion: '',
                                        tipo_evento: '',
                                        tags: ''
                                      });
                                      setIsSaveTemplateOpen(true);
                                    }}
                                    size="sm"
                                    className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                                    disabled={!currentPlaylist.lista_personalizada_generada || currentPlaylist.lista_personalizada_generada.length === 0}
                                  >
                                    <Sparkles className="w-4 h-4 sm:mr-2" />
                                    <span className="hidden sm:inline">Plantilla</span>
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      fetchPlaylistTemplates();
                                      fetchSmartPlaylistTemplates();
                                      setIsApplyTemplateOpen(true);
                                    }}
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                  >
                                    <FileText className="w-4 h-4 sm:mr-2" />
                                    <span className="hidden sm:inline">Aplicar</span>
                                  </Button>
                                  {user?.role === 'admin' && (
                                    <Button
                                      onClick={handleExportToSpotify}
                                      size="sm"
                                      className="bg-spotify hover:bg-spotify/90 text-white text-xs"
                                      style={{ backgroundColor: '#1DB954' }}
                                      disabled={!accessToken || !currentPlaylist.lista_personalizada_generada || currentPlaylist.lista_personalizada_generada.length === 0}
                                      title={!accessToken ? 'Conecta con Spotify primero' : 'Exportar como playlist de Spotify'}
                                      data-testid="export-custom-list-spotify-btn"
                                    >
                                      <Music className="w-4 h-4 sm:mr-2" />
                                      <span className="hidden sm:inline">Spotify</span>
                                    </Button>
                                  )}
                                  <Button
                                    onClick={() => handleExportCustomListPDF(currentPlaylist.id)}
                                    size="sm"
                                    className="bg-red-600 hover:bg-red-700 text-white text-xs"
                                  >
                                    <Download className="w-4 h-4 sm:mr-2" />
                                    <span className="hidden sm:inline">PDF</span>
                                  </Button>
                                  <Button
                                    onClick={() => handleExportToEngineDJ(currentPlaylist?.id)}
                                    size="sm"
                                    className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                                  >
                                    <Download className="w-4 h-4 sm:mr-2" />
                                    <span className="hidden sm:inline">Engine DJ</span>
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Sparkline de energía (BPM) de la playlist */}
                          {!isLiveMode && generatedCustomList?.length > 1 && (
                            <PlaylistEnergySparkline songs={generatedCustomList} />
                          )}

                          {/* Filtros de búsqueda para Lista Personalizada */}
                          <div className={`bg-slate-900/50 ${isLiveMode ? 'p-2' : 'p-4'} rounded-lg mb-4`}>
                            {!isLiveMode && (
                              <h4 className="text-white text-sm font-semibold mb-3">🔍 Buscar en esta lista</h4>
                            )}
                            <div className={`grid grid-cols-1 ${isLiveMode ? '' : 'md:grid-cols-2 lg:grid-cols-4'} gap-3`}>
                              {/* Campo de búsqueda */}
                              <input
                                type="text"
                                placeholder={isLiveMode ? '🔍 Buscar canción...' : 'Buscar título o artista...'}
                                value={listaPersonalizadaSearch}
                                onChange={(e) => setListaPersonalizadaSearch(e.target.value)}
                                className={`px-3 ${isLiveMode ? 'py-3 text-base' : 'py-2 text-sm'} bg-slate-800 border border-slate-700 text-white rounded focus:ring-2 focus:ring-cyan-500`}
                                data-testid="custom-list-search-input"
                              />

                              {/* Modo En Vivo: acceso rápido a "Ocultar reproducidas"
                                  (siempre visible, sin depender del banner). */}
                              {isLiveMode && (
                                <button
                                  onClick={() => setHidePlayedSongs((v) => !v)}
                                  className={`flex items-center justify-center gap-2 px-3 py-3 rounded text-base font-medium transition-colors ${
                                    hidePlayedSongs
                                      ? 'bg-amber-600/30 border border-amber-500/60 text-amber-200'
                                      : 'bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700'
                                  }`}
                                  data-testid="live-toggle-hide-played-btn"
                                  aria-pressed={hidePlayedSongs}
                                >
                                  {hidePlayedSongs ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                  {hidePlayedSongs ? 'Mostrando solo pendientes' : 'Ocultar reproducidas'}
                                </button>
                              )}
                              
                              {/* Filtros dropdown — ocultos en Live Mode para reducir ruido visual */}
                              {!isLiveMode && (
                                <>
                                  <select
                                    value={listaPersonalizadaFilterGenre}
                                    onChange={(e) => setListaPersonalizadaFilterGenre(e.target.value)}
                                    className="px-3 py-2 bg-slate-800 border border-slate-700 text-white text-sm rounded focus:ring-2 focus:ring-cyan-500"
                                  >
                                    <option value="all">Todos los estilos</option>
                                    {getAllGenres().map(genre => (
                                      <option key={genre} value={genre}>{genre}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={listaPersonalizadaFilterMomento}
                                    onChange={(e) => setListaPersonalizadaFilterMomento(e.target.value)}
                                    className="px-3 py-2 bg-slate-800 border border-slate-700 text-white text-sm rounded focus:ring-2 focus:ring-cyan-500"
                                  >
                                    <option value="all">Todos los momentos</option>
                                    {getAllMomentos().map(momento => (
                                      <option key={momento} value={momento}>{momento}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={listaPersonalizadaFilterEnergia}
                                    onChange={(e) => setListaPersonalizadaFilterEnergia(e.target.value)}
                                    className="px-3 py-2 bg-slate-800 border border-slate-700 text-white text-sm rounded focus:ring-2 focus:ring-cyan-500"
                                  >
                                    <option value="all">Todas las energías</option>
                                    <option value="Baja">Baja</option>
                                    <option value="Media">Media</option>
                                    <option value="Alta">Alta</option>
                                  </select>
                                </>
                              )}
                            </div>
                            
                            {/* Botón limpiar filtros */}
                            <div className="flex flex-wrap gap-2 mt-3">
                              {(listaPersonalizadaSearch || listaPersonalizadaFilterGenre !== "all" || 
                                listaPersonalizadaFilterMomento !== "all" || listaPersonalizadaFilterEnergia !== "all") && (
                                <Button
                                  onClick={() => {
                                    setListaPersonalizadaSearch("");
                                    setListaPersonalizadaFilterGenre("all");
                                    setListaPersonalizadaFilterMomento("all");
                                    setListaPersonalizadaFilterEnergia("all");
                                  }}
                                  size="sm"
                                  variant="ghost"
                                  className="text-slate-400 hover:text-white text-xs"
                                >
                                  Limpiar
                                </Button>
                              )}
                              
                              {/* Toggle view mode - 3 opciones */}
                              <div className="flex gap-1">
                                <Button
                                  onClick={() => setListaPersonalizadaViewMode('list')}
                                  size="sm"
                                  variant={listaPersonalizadaViewMode === 'list' ? 'default' : 'outline'}
                                  className={`text-xs px-2 ${listaPersonalizadaViewMode === 'list' ? 'bg-cyan-600 hover:bg-cyan-700' : 'border-slate-600 text-white hover:bg-slate-700'}`}
                                  data-testid="view-mode-list"
                                >
                                  Lista
                                </Button>
                                <Button
                                  onClick={() => setListaPersonalizadaViewMode('sections')}
                                  size="sm"
                                  variant={listaPersonalizadaViewMode === 'sections' ? 'default' : 'outline'}
                                  className={`text-xs px-2 ${listaPersonalizadaViewMode === 'sections' ? 'bg-cyan-600 hover:bg-cyan-700' : 'border-slate-600 text-white hover:bg-slate-700'}`}
                                  data-testid="view-mode-sections"
                                >
                                  Momentos
                                </Button>
                                <Button
                                  onClick={() => setListaPersonalizadaViewMode('energy')}
                                  size="sm"
                                  variant={listaPersonalizadaViewMode === 'energy' ? 'default' : 'outline'}
                                  className={`text-xs px-2 ${listaPersonalizadaViewMode === 'energy' ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700' : 'border-slate-600 text-white hover:bg-slate-700'}`}
                                  data-testid="view-mode-energy"
                                >
                                  Energia
                                </Button>
                              </div>
                              
                              {/* Botones Cadenas - en grupo separado para móvil */}
                              <div className="flex gap-1">
                                {/* Botón Cadenas de Mezcla */}
                                <Button
                                  onClick={() => {
                                    setMixChainsMode('browse');
                                    setIsMixChainsOpen(true);
                                  }}
                                  size="sm"
                                  variant="outline"
                                  className="border-cyan-600/50 text-cyan-400 hover:bg-cyan-600/20 text-xs px-2"
                                  data-testid="open-mix-chains-btn"
                                >
                                  <svg className="w-3 h-3 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                  </svg>
                                  <span className="hidden sm:inline">Cadenas</span>
                                </Button>
                                
                                {/* Botón Guardar como Cadena (si hay canciones) */}
                                {generatedCustomList && generatedCustomList.length >= 2 && (
                                  <Button
                                    onClick={() => {
                                      setSelectedSongsForChain(generatedCustomList);
                                      setMixChainsMode('create');
                                      setIsMixChainsOpen(true);
                                    }}
                                    size="sm"
                                    variant="outline"
                                    className="border-amber-600/50 text-amber-400 hover:bg-amber-600/20 text-xs px-2"
                                    data-testid="save-as-chain-btn"
                                  >
                                    <svg className="w-3 h-3 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                    </svg>
                                    <span className="hidden sm:inline">Guardar</span>
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Análisis de Géneros */}
                          {showGenreAnalysis && (
                            <div className="mb-4">
                              <React.Suspense fallback={<div className="text-slate-400 text-sm py-8 text-center">Cargando análisis de géneros...</div>}>
                              <GenreAnalysis
                                songs={generatedCustomList || []}
                                showRecommendations={true}
                              />
                              </React.Suspense>
                            </div>
                          )}

                          {/* Vista de Curva de Energía */}
                          {listaPersonalizadaViewMode === 'energy' && currentPlaylist && (
                            <div className="mb-4">
                              <EnergyCurve 
                                playlistId={currentPlaylist.id}
                                token={token}
                                onListUpdated={async (_newOrder) => {
                                  // Refrescar la playlist para obtener el nuevo orden
                                  try {
                                    const response = await axios.get(`${API}/playlists/${currentPlaylist.id}`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    // Actualizar la lista de canciones generada
                                    if (response.data.lista_personalizada_generada) {
                                      const songsResponse = await axios.post(
                                        `${API}/songs/by-ids`,
                                        { ids: response.data.lista_personalizada_generada },
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      setGeneratedCustomList(songsResponse.data.songs || []);
                                    }
                                  } catch (error) {
                                    console.error('Error refreshing playlist:', error);
                                  }
                                }}
                              />
                            </div>
                          )}

                          {/* Vistas: Lista o Secciones */}
                          <>
                          {/* Contador + Reiniciar marcas (feature Feb 2026) */}
                          {(() => {
                            const playedCount = Object.values(customListPlayedState).filter((s) => s?.played).length;
                            if (playedCount === 0) return null;
                            return (
                              <div className="flex items-center justify-between p-2 bg-emerald-900/20 border border-emerald-700/40 rounded mb-2 gap-2">
                                <p className="text-emerald-300 text-xs sm:text-sm">
                                  <CheckCircle2 className="w-4 h-4 inline mr-1" />
                                  {playedCount} {playedCount === 1 ? 'canción marcada' : 'canciones marcadas'} como {playedCount === 1 ? 'reproducida' : 'reproducidas'}
                                </p>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  <button
                                    onClick={() => setHidePlayedSongs((v) => !v)}
                                    className={`text-xs underline transition-colors ${hidePlayedSongs ? 'text-amber-300 hover:text-amber-200' : 'text-emerald-400 hover:text-emerald-200'}`}
                                    data-testid="custom-list-toggle-hide-played-btn"
                                    title={hidePlayedSongs ? 'Volver a mostrar las reproducidas' : 'Ocultar las reproducidas de la vista'}
                                  >
                                    {hidePlayedSongs ? 'Mostrar reproducidas' : 'Ocultar reproducidas'}
                                  </button>
                                  <button
                                    onClick={handleResetCustomListPlayed}
                                    className="text-xs text-emerald-400 hover:text-emerald-200 underline"
                                    data-testid="custom-list-reset-played-btn"
                                  >
                                    Reiniciar marcas
                                  </button>
                                </div>
                              </div>
                            );
                          })()}
                          {/* Vista por Lista (normal) con Drag & Drop */}
                          {listaPersonalizadaViewMode === 'list' && (
                            <SortableSongList
                              songs={filteredCustomList.filter(song => {
                                  if (!song) return false;

                                  // Ocultar reproducidas si el toggle está activo
                                  if (hidePlayedSongs && customListPlayedState[song.id]?.played) {
                                    return false;
                                  }

                                  // Filtro de búsqueda (accent-insensitive)
                                  if (listaPersonalizadaSearch) {
                                    const searchNorm = normalizeText(listaPersonalizadaSearch);
                                    const titleNorm = normalizeText(song.title || '');
                                    const artistNorm = normalizeText(song.artist || '');
                                    if (!titleNorm.includes(searchNorm) && !artistNorm.includes(searchNorm)) {
                                      return false;
                                    }
                                  }
                                  
                                  // Filtro por género
                                  if (listaPersonalizadaFilterGenre !== "all") {
                                    if (!song.genres || !song.genres.includes(listaPersonalizadaFilterGenre)) {
                                      return false;
                                    }
                                  }
                                  
                                  // Filtro por momento
                                  if (listaPersonalizadaFilterMomento !== "all") {
                                    if (!song.momentos || !song.momentos.includes(listaPersonalizadaFilterMomento)) {
                                      return false;
                                    }
                                  }
                                  
                                  // Filtro por energía
                                  if (listaPersonalizadaFilterEnergia !== "all") {
                                    if (song.energia !== listaPersonalizadaFilterEnergia) {
                                      return false;
                                    }
                                  }
                                  
                                  return true;
                                }).map(s => enrichSongWithMarkers(s, currentPlaylist?.playlist_markers))}
                              onReorder={handleReorderCustomList}
                              orderedIds={(generatedCustomList || []).map(s => s.id)}
                              dragDisabled={!!(hidePlayedSongs || listaPersonalizadaSearch || listaPersonalizadaFilterGenre !== "all" || listaPersonalizadaFilterMomento !== "all" || listaPersonalizadaFilterEnergia !== "all")}
                              onMoveToPosition={(songId, targetGlobalIndex) => {
                                // Mueve sobre la lista COMPLETA (preserva todas las
                                // canciones aunque haya un filtro activo).
                                const items = [...(generatedCustomList || [])];
                                const from = items.findIndex(s => s.id === songId);
                                if (from === -1) return;
                                const clamped = Math.max(0, Math.min(items.length - 1, targetGlobalIndex));
                                if (from === clamped) return;
                                const [moved] = items.splice(from, 1);
                                items.splice(clamped, 0, moved);
                                handleReorderCustomList(items);
                              }}
                              onRemove={handleRemoveSongFromCustomList}
                              showDetails={true}
                              showNumbers={true}
                              onMixSuggestions={(song) => {
                                setMixSuggestionsSong(song);
                                setIsMixSuggestionsOpen(true);
                              }}
                              playedState={customListPlayedState}
                              onTogglePlayed={handleToggleCustomListPlayed}
                              isLiveMode={isLiveMode}
                              onEditSong={handleLongPressEditSong}
                            />
                          )}

                          {/* Vista por Secciones/Momentos con Drag & Drop ENTRE momentos */}
                          {listaPersonalizadaViewMode === 'sections' && (
                            <MomentosSortableView
                              groupedSongs={(() => {
                                const grouped = groupSongsByMomentos(currentPlaylist.lista_personalizada_generada || []);
                                if (!hidePlayedSongs) return grouped;
                                // Ocultar reproducidas (sin tocar datos). Con esto activo
                                // el arrastre se desactiva para no perder canciones al reordenar.
                                const out = {};
                                for (const k of Object.keys(grouped)) {
                                  out[k] = (grouped[k] || []).filter(s => !customListPlayedState[s.id]?.played);
                                }
                                return out;
                              })()}
                              onReorderGlobal={handleReorderMomentosGlobal}
                              dragDisabled={hidePlayedSongs}
                              onRemove={handleRemoveSongFromCustomList}
                              showDetails={true}
                              playedState={customListPlayedState}
                              onTogglePlayed={handleToggleCustomListPlayed}
                              isLiveMode={isLiveMode}
                              onEditSong={handleLongPressEditSong}
                            />
                          )}
                          </>

                          {/* Botón Generar Recomendaciones IA - MOVIDO AQUÍ */}
                          {!isLiveMode && (
                            <>
                              <Button
                                onClick={() => handleGenerateAIRecommendationsForEvent(currentPlaylist.id)}
                                disabled={isGeneratingRecommendations}
                                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white touch-manipulation mt-4"
                              >
                                {isGeneratingRecommendations ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Generando Recomendaciones...
                                  </>
                                ) : (
                                  <>
                                    ✨ Generar Recomendaciones IA
                                  </>
                                )}
                              </Button>
                              {currentPlaylist.tipo_evento && (
                                <p className="text-xs text-slate-400 mt-1 text-center">
                                  📍 Basado en: "{currentPlaylist.tipo_evento}"
                            </p>
                          )}
                          {!currentPlaylist.tipo_evento && (
                            <p className="text-xs text-amber-400/80 mt-1 text-center">
                              💡 Edita el evento y añade nacionalidades/edades en "Tipo de Evento" para mejores resultados
                            </p>
                          )}

                          {/* Mostrar Recomendaciones IA generadas */}
                          {currentPlaylist.ai_recommendations && currentPlaylist.ai_recommendations.length > 0 && (
                            <Card className="bg-gradient-to-br from-purple-900/30 to-pink-900/20 border-purple-700/50 mt-4">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-purple-300 text-sm flex items-center justify-between">
                                  <span>✨ Recomendaciones IA ({currentPlaylist.ai_recommendations.length})</span>
                                  {currentPlaylist.tipo_evento && (
                                    <span className="text-xs text-slate-400 font-normal">
                                      Basadas en "{currentPlaylist.tipo_evento}"
                                    </span>
                                  )}
                                </CardTitle>
                                {/* Country balance summary */}
                                {(() => {
                                  const countryFlags = {
                                    'france': '🇫🇷', 'norway': '🇳🇴', 'spain': '🇪🇸', 'sweden': '🇸🇪',
                                    'germany': '🇩🇪', 'italy': '🇮🇹', 'uk': '🇬🇧', 'usa': '🇺🇸',
                                    'mexico': '🇲🇽', 'brazil': '🇧🇷', 'argentina': '🇦🇷', 'colombia': '🇨🇴',
                                    'portugal': '🇵🇹', 'denmark': '🇩🇰', 'finland': '🇫🇮', 'latin': '🌎', 'international': '🌍'
                                  };
                                  const countryCounts = {};
                                  currentPlaylist.ai_recommendations.forEach(rec => {
                                    if (rec.country) {
                                      countryCounts[rec.country] = (countryCounts[rec.country] || 0) + 1;
                                    }
                                  });
                                  const countries = Object.entries(countryCounts);
                                  if (countries.length > 1) {
                                    return (
                                      <div className="flex gap-3 mt-2 flex-wrap">
                                        {countries.map(([country, count]) => (
                                          <span key={country} className="text-xs bg-slate-800/60 text-slate-300 px-2 py-1 rounded-full flex items-center gap-1">
                                            <span>{countryFlags[country] || '🎵'}</span>
                                            <span className="capitalize">{country}</span>
                                            <span className="text-purple-400 font-medium">({count})</span>
                                          </span>
                                        ))}
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                  {currentPlaylist.ai_recommendations.map((rec, index) => {
                                    // Country flag mapping
                                    const countryFlags = {
                                      'france': '🇫🇷',
                                      'norway': '🇳🇴',
                                      'spain': '🇪🇸',
                                      'sweden': '🇸🇪',
                                      'germany': '🇩🇪',
                                      'italy': '🇮🇹',
                                      'uk': '🇬🇧',
                                      'usa': '🇺🇸',
                                      'mexico': '🇲🇽',
                                      'brazil': '🇧🇷',
                                      'argentina': '🇦🇷',
                                      'colombia': '🇨🇴',
                                      'portugal': '🇵🇹',
                                      'denmark': '🇩🇰',
                                      'finland': '🇫🇮',
                                      'latin': '🌎',
                                      'international': '🌍'
                                    };
                                    const countryFlag = rec.country ? countryFlags[rec.country] || '🎵' : '';
                                    const countryName = rec.country ? rec.country.charAt(0).toUpperCase() + rec.country.slice(1) : '';
                                    
                                    return (
                                    <div key={rec.id || rec.track_id || rec.spotify_id || `${rec.title}-${rec.artist}-${index}`} className="bg-slate-900/50 p-3 rounded-lg flex items-center justify-between gap-3">
                                      {/* Status icon with country flag */}
                                      <div className="flex-shrink-0 relative">
                                        {rec.in_library ? (
                                          <div className="w-8 h-8 rounded-full bg-green-600/30 flex items-center justify-center" title="En tu biblioteca">
                                            <Music className="w-4 h-4 text-green-400" />
                                          </div>
                                        ) : (
                                          <div className="w-8 h-8 rounded-full bg-orange-600/30 flex items-center justify-center" title="Solo en Spotify">
                                            <ExternalLink className="w-4 h-4 text-orange-400" />
                                          </div>
                                        )}
                                        {/* Country flag badge */}
                                        {countryFlag && (
                                          <span 
                                            className="absolute -top-1 -right-1 text-xs" 
                                            title={countryName}
                                          >
                                            {countryFlag}
                                          </span>
                                        )}
                                      </div>
                                      
                                      {/* Song info */}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-white font-medium text-sm truncate">{rec.title}</p>
                                        <p className="text-slate-400 text-xs truncate">{rec.artist}</p>
                                        {rec.reason && (
                                          <p className="text-purple-300 text-xs mt-1 italic truncate">💡 {rec.reason}</p>
                                        )}
                                        {rec.genres && rec.genres.length > 0 && (
                                          <div className="flex gap-1 mt-1 flex-wrap">
                                            {rec.genres.slice(0, 2).map((genre, gi) => (
                                              <span key={gi} className="text-xs bg-slate-700/50 text-slate-300 px-1.5 py-0.5 rounded">
                                                {genre}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Action buttons */}
                                      <div className="flex gap-2 flex-shrink-0">
                                        {/* Add to Pendientes Denon button - only if NOT in library */}
                                        {!rec.in_library && (
                                          <Button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              try {
                                                // Check if already in library (by title/artist)
                                                const exists = songs.some(s => 
                                                  s.title?.toLowerCase() === rec.title?.toLowerCase() && 
                                                  s.artist?.toLowerCase() === rec.artist?.toLowerCase()
                                                );
                                                
                                                if (exists) {
                                                  toast.info('Esta canción ya está en tu biblioteca');
                                                  return;
                                                }
                                                
                                                // Add to library with pendiente_denon = true
                                                const newSong = {
                                                  title: rec.title,
                                                  artist: rec.artist,
                                                  spotify_url: rec.spotify_url,
                                                  genres: rec.genres || [],
                                                  rating: null,
                                                  momentos: [],
                                                  notas: `Recomendación IA para ${currentPlaylist.nombre_novios || 'evento'}`,
                                                  pendiente_denon: true
                                                };
                                                
                                                await createSong(newSong);
                                                toast.success('✅ Añadida a biblioteca como PENDIENTE DENON 🔶');
                                                
                                                // Update recommendation in UI to show it's now in library
                                                // This will refresh after fetch
                                                await fetchDenonPendientesCount();
                                              } catch (error) {
                                                console.error('Error adding to library:', error);
                                                toast.error('Error al añadir a biblioteca');
                                              }
                                            }}
                                            size="sm"
                                            className="bg-orange-600 hover:bg-orange-700 text-white h-8 px-2 touch-manipulation"
                                            title="Añadir a Pendientes Denon"
                                          >
                                            <Plus className="w-4 h-4" />
                                          </Button>
                                        )}
                                        
                                        {/* In library indicator with link to song */}
                                        {rec.in_library && (
                                          <div className="flex items-center">
                                            <span className="text-xs text-green-400 mr-2">✓ En biblioteca</span>
                                          </div>
                                        )}
                                        
                                        {/* Spotify link */}
                                        {rec.spotify_url && (
                                          <Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              window.open(rec.spotify_url, '_blank');
                                            }}
                                            size="sm"
                                            className="bg-green-600 hover:bg-green-700 text-white h-8 px-2 touch-manipulation"
                                            title="Abrir en Spotify"
                                          >
                                            <ExternalLink className="w-4 h-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  )})}
                                </div>
                                
                                {/* Legend */}
                                <div className="flex gap-4 mt-4 pt-3 border-t border-slate-700/50 text-xs text-slate-400">
                                  <div className="flex items-center gap-1">
                                    <div className="w-4 h-4 rounded-full bg-green-600/30 flex items-center justify-center">
                                      <Music className="w-2.5 h-2.5 text-green-400" />
                                    </div>
                                    <span>En biblioteca</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <div className="w-4 h-4 rounded-full bg-orange-600/30 flex items-center justify-center">
                                      <ExternalLink className="w-2.5 h-2.5 text-orange-400" />
                                    </div>
                                    <span>Solo Spotify</span>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          )}
                          </>
                          )}

                          {/* Botones de Enviar por Momento - AHORA AQUÍ */}
                          {spotifyConnected && currentPlaylist.lista_personalizada_generada && (
                            <Card className="bg-green-900/20 border-green-700/30 mt-4">
                              <CardHeader className="pb-3">
                                <CardTitle className="text-green-300 text-sm flex items-center gap-2">
                                  <Music className="w-4 h-4" />
                                  Enviar a Spotify por Momento
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                  {MOMENTOS_BODA.map((momento) => {
                                    const momentoSongs = currentPlaylist.lista_personalizada_generada
                                      .map(songId => songs.find(s => s.id === songId))
                                      .filter(song => song && song.momentos && song.momentos.includes(momento));
                                    
                                    return (
                                      <Button
                                        key={momento}
                                        onClick={() => toast.info(`Función de envío por momento próximamente`)}
                                        disabled={momentoSongs.length === 0}
                                        size="sm"
                                        className="bg-green-700/30 hover:bg-green-700/50 text-green-200 border border-green-600/30 text-xs"
                                      >
                                        {momento} ({momentoSongs.length})
                                      </Button>
                                    );
                                  })}
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Tab: Peticiones */}
              {currentTab === 'peticiones' && isLiveMode && (
                <LiveDashboard
                  peticiones={currentPlaylist?.peticiones || []}
                  playlistId={currentPlaylist?.id}
                  token={token}
                  activeBucket={activeMomentoBucket}
                  onActiveBucketChange={setActiveMomentoBucket}
                  momentoOverrides={momentoOverrides}
                />
              )}
              {currentTab === 'peticiones' && !isLiveMode && (
                <div className="space-y-4">
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <CardTitle className="text-white flex flex-wrap items-center gap-2">
                          🎤 Peticiones de Canciones
                          {cacheStats.totalCount > 0 && (
                            <span className="text-xs bg-green-900/30 text-green-400 px-2 py-1 rounded whitespace-nowrap">
                              📦 {cacheStats.totalCount} en caché ({cacheStats.totalSizeMB}MB)
                            </span>
                          )}
                        </CardTitle>
                        <div className="flex flex-wrap gap-2">
                          {/* Controles offline por evento (reutilizable). Borrado solo para eventos pasados. */}
                          <EventOfflineControls
                            playlistId={currentPlaylist?.id}
                            eventDate={currentPlaylist?.fecha_evento}
                            peticiones={currentPlaylist?.peticiones}
                          />
                          {/* Preparar YouTube (bulk auto-search + descarga) */}
                          <BulkYouTubePrepareButton
                            playlistId={currentPlaylist?.id}
                            peticiones={currentPlaylist?.peticiones}
                            onSuccess={async () => {
                              // Refrescar la playlist tras terminar el job
                              try {
                                const t = localStorage.getItem('token');
                                const resp = await axios.get(`${API}/playlists/${currentPlaylist.id}`, {
                                  headers: { Authorization: `Bearer ${t}` },
                                });
                                setCurrentPlaylist(resp.data);
                              } catch (err) {
                                console.error('Error refrescando playlist tras bulk-yt:', err);
                              }
                            }}
                          />
                          <Button
                            onClick={() => setIsAudioUploaderOpen(true)}
                            size="sm"
                            className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                            data-testid="open-subir-audio-btn"
                          >
                            <Upload className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Subir Audio</span>
                          </Button>
                          <Button
                            onClick={() => setIsPeticionesDialogOpen(true)}
                            size="sm"
                            className="bg-pink-600 hover:bg-pink-700 text-white text-xs"
                            data-testid="open-busqueda-btn"
                          >
                            <Plus className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Búsqueda</span>
                          </Button>
                          {/* Botón exportar peticiones a Spotify */}
                          {currentPlaylist?.peticiones?.length > 0 && spotifyConnected && (
                            <Button
                              onClick={async () => {
                                if (!accessToken) {
                                  toast.error('Conecta con Spotify primero');
                                  return;
                                }
                                
                                const confirmed = window.confirm(
                                  `¿Exportar ${currentPlaylist.peticiones.length} peticiones a una nueva playlist de Spotify?\n\n` +
                                  `Se creará una playlist llamada "Peticiones - ${currentPlaylist.nombre_novios}"`
                                );
                                
                                if (!confirmed) return;
                                
                                const toastId = toast.loading('Exportando peticiones a Spotify...');
                                
                                try {
                                  const response = await axios.post(
                                    `${API}/playlists/${currentPlaylist.id}/export-peticiones-to-spotify`,
                                    {},
                                    {
                                      params: { access_token: accessToken },
                                      headers: { Authorization: `Bearer ${token}` }
                                    }
                                  );
                                  
                                  const { playlist_url, tracks_added, tracks_not_found, not_found_list } = response.data;
                                  
                                  let message = `✅ Playlist creada con ${tracks_added} canciones`;
                                  if (tracks_not_found > 0) {
                                    message += `\n⚠️ ${tracks_not_found} no encontradas en Spotify`;
                                  }
                                  
                                  toast.success(message, { id: toastId, duration: 5000 });
                                  
                                  // Show not found songs if any
                                  if (not_found_list && not_found_list.length > 0) {
                                    setTimeout(() => {
                                      toast.info(`No encontradas: ${not_found_list.slice(0, 3).join(', ')}${not_found_list.length > 3 ? '...' : ''}`, { duration: 8000 });
                                    }, 1000);
                                  }
                                  
                                  // Open playlist in new tab
                                  window.open(playlist_url, '_blank');
                                  
                                } catch (error) {
                                  console.error('Error exporting to Spotify:', error);
                                  const errorMsg = error.response?.data?.detail || error.message;
                                  toast.error(`Error: ${errorMsg}`, { id: toastId });
                                }
                              }}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white text-xs"
                              title="Exportar peticiones a playlist de Spotify"
                            >
                              <ExternalLink className="w-4 h-4 sm:mr-2" />
                              <span className="hidden sm:inline">A Spotify</span>
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-slate-400 text-sm mt-2">
                        Gestiona las peticiones de canciones para este evento con notas personalizadas
                      </p>
                      {/* Subpestañas por momento + botón Importar Spotify */}
                      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-700/50">
                        <MomentoSubTabs
                          peticiones={currentPlaylist?.peticiones || []}
                          activeBucket={activeMomentoBucket}
                          onChange={setActiveMomentoBucket}
                          overrides={momentoOverrides}
                          onOverridesChange={handleMomentoOverridesChange}
                        />
                        <Button
                          onClick={() => setSpotifyImportOpen(true)}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                          title={`Importar una playlist de Spotify a "${BUCKET_LABELS[activeMomentoBucket]}"`}
                          data-testid="import-spotify-bucket-btn"
                        >
                          <ExternalLink className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">
                            Importar Spotify → {BUCKET_LABELS[activeMomentoBucket]}
                          </span>
                          <span className="sm:hidden">Spotify</span>
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Lista de peticiones con drag-and-drop (filtrada por bucket activo) */}
                      {(!currentPlaylist.peticiones || currentPlaylist.peticiones.length === 0) ? (
                        <div className="text-center py-12">
                          <Music className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                          <p className="text-slate-400">No hay peticiones registradas</p>
                          <p className="text-slate-500 text-sm mt-2">
                            Añade canciones con notas para organizar las peticiones
                          </p>
                        </div>
                      ) : filteredPeticionesByBucket.length === 0 ? (
                        <div className="text-center py-12" data-testid="peticiones-empty-bucket">
                          <Music className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                          <p className="text-slate-400">
                            No hay peticiones en <span className="text-white font-semibold">{BUCKET_LABELS[activeMomentoBucket]}</span>
                          </p>
                          <p className="text-slate-500 text-sm mt-2">
                            Añade una petición o impórtala desde Spotify — se etiquetará automáticamente en este momento.
                          </p>
                        </div>
                      ) : (
                        <DragDropContext onDragEnd={(result) => handlePeticionesDragEnd(result, (p) => guessBucketFromPeticion(p) === activeMomentoBucket)}>
                          <Droppable droppableId="peticiones-list">
                            {(provided) => (
                              <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                className="space-y-3"
                              >
                                {filteredPeticionesByBucket.map(({ p: peticion, originalIndex }, localIndex) => (
                                  <SortablePeticionItem
                                    key={`peticion-${originalIndex}`}
                                    peticion={peticion}
                                    index={localIndex}
                                    onRemove={() => handleRemovePeticion(currentPlaylist.id, originalIndex)}
                                    onToggleHidden={() => handleTogglePeticionHidden(currentPlaylist.id, originalIndex)}
                                    playlistId={currentPlaylist.id}
                                    onPlaylistRefresh={async () => {
                                      try {
                                        const token = localStorage.getItem('token');
                                        const resp = await axios.get(`${API}/playlists/${currentPlaylist.id}`, {
                                          headers: { 'Authorization': `Bearer ${token}` }
                                        });
                                        setCurrentPlaylist(resp.data);
                                      } catch (err) {
                                        console.error('Error refreshing playlist:', err);
                                      }
                                    }}
                                    peticionesPendingFlags={peticionesPendingFlags}
                                    setPeticionesPendingFlags={setPeticionesPendingFlags}
                                    songs={songs}
                                    createSong={createSong}
                                    playingAudioId={playingAudioId}
                                    setPlayingAudioId={setPlayingAudioId}
                                    audioStates={audioStates}
                                    setAudioStates={setAudioStates}
                                    audioProgress={audioProgress}
                                    setAudioProgress={setAudioProgress}
                                    preloadAudio={preloadAudio}
                                    cachedAudios={cachedAudios}
                                  />
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Tab: Playlists Cliente */}
              {currentTab === 'playlists-cliente' && (
                <ClientSpotifyPlaylists
                  playlistId={currentPlaylist.id}
                  externalPlayedKeys={externalPlayedKeys}
                  onSongAdded={(_newSong) => {
                    // Refresh songs list when a song is added
                    toast.success('Canción añadida a la biblioteca');
                  }}
                />
              )}

              {/* Tab: Peticiones en Vivo - Live Mode 2.0 */}
              {currentTab === 'peticiones-vivo' && (
                <div className="mt-4">
                  <LiveMode2
                    currentPlaylist={currentPlaylist}
                    token={token}
                    songs={songs}
                    activeBucket={activeMomentoBucket}
                    onActiveBucketChange={setActiveMomentoBucket}
                    momentoOverrides={momentoOverrides}
                    onAddToCustomList={(songId) => {
                      // Add song to custom list
                      if (currentPlaylist && songId) {
                        handleAddSongToPlaylist(songId, currentPlaylist.id);
                      }
                    }}
                  />
                </div>
              )}

              {/* Tab: Notas Compartidas */}
              {currentTab === 'notas' && (
                <div className="space-y-4">
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center justify-between">
                        <span>📝 Notas Compartidas</span>
                        <Button
                          onClick={() => {
                            setCurrentNote({ content: '', visible_to_client: false, tipo: 'general', priority: 'normal' });
                            setEditingNoteId(null);
                            setIsNotesDialogOpen(true);
                          }}
                          className="bg-sky-600 hover:bg-sky-700"
                          size="sm"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Nueva Nota
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {sharedNotes.length === 0 ? (
                        <div className="text-center py-8 text-slate-400">
                          <p className="text-lg mb-2">No hay notas compartidas</p>
                          <p className="text-sm">Crea una nota para compartir información importante del evento</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {sharedNotes.map((note) => (
                            <div
                              key={note.id}
                              className={`p-4 rounded-lg border ${
                                note.priority === 'urgent' ? 'bg-red-900/20 border-red-600' :
                                note.priority === 'high' ? 'bg-orange-900/20 border-orange-600' :
                                'bg-slate-900/50 border-slate-700'
                              }`}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      note.tipo === 'logistica' ? 'bg-blue-600/30 text-blue-300' :
                                      note.tipo === 'musical' ? 'bg-purple-600/30 text-purple-300' :
                                      note.tipo === 'tecnica' ? 'bg-yellow-600/30 text-yellow-300' :
                                      'bg-slate-600/30 text-slate-300'
                                    }`}>
                                      {note.tipo === 'logistica' ? '📦 Logística' :
                                       note.tipo === 'musical' ? '🎵 Musical' :
                                       note.tipo === 'tecnica' ? '⚙️ Técnica' :
                                       '📋 General'}
                                    </span>
                                    {note.priority !== 'normal' && (
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        note.priority === 'urgent' ? 'bg-red-600 text-white' :
                                        note.priority === 'high' ? 'bg-orange-600 text-white' :
                                        'bg-slate-600 text-white'
                                      }`}>
                                        {note.priority === 'urgent' ? '🚨 Urgente' :
                                         note.priority === 'high' ? '⚠️ Alta' :
                                         'Normal'}
                                      </span>
                                    )}
                                    {note.visible_to_client && (
                                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-600/30 text-green-300">
                                        👁️ Visible para cliente
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-slate-300 text-sm whitespace-pre-wrap">{note.content}</p>
                                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                                    <span>Por: {note.author}</span>
                                    <span>•</span>
                                    <span>{new Date(note.created_at).toLocaleString('es-ES')}</span>
                                    {note.updated_at && (
                                      <>
                                        <span>•</span>
                                        <span>Editado: {new Date(note.updated_at).toLocaleString('es-ES')}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-2 ml-4">
                                  <Button
                                    onClick={() => handleEditNote(note)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-sky-400 hover:text-sky-300 hover:bg-sky-900/20"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteNote(note.id)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Tab: Escaleta / Timeline */}
              {currentTab === 'escaleta' && (
                <EscaletaTab
                  playlist={currentPlaylist}
                  onUpdate={(updatedPlaylist) => setCurrentPlaylist(updatedPlaylist)}
                />
              )}

              {/* Tab: Checklist */}
              {currentTab === 'checklist' && (
                <div className="space-y-4">
                  <ChecklistOnboarding 
                    playlist={currentPlaylist}
                    onUpdate={(newChecklist) => {
                      setCurrentPlaylist({
                        ...currentPlaylist,
                        checklist_onboarding: newChecklist
                      });
                    }}
                    isAdmin={user?.role === 'admin'}
                    API={API}
                  />
                </div>
              )}

              {/* Tab: Documentos */}
              {currentTab === 'documentos' && (
                <div className="space-y-4">
                  <GestionDocumentos 
                    playlist={currentPlaylist}
                    onUpdate={(updatedPlaylist) => {
                      setCurrentPlaylist(updatedPlaylist);
                    }}
                    API={API}
                  />
                </div>
              )}

              {/* Tab: Presupuestos */}
              {currentTab === 'presupuestos' && user?.role === 'admin' && (
                <div className="space-y-4">
                  <GestionPresupuestos 
                    playlist={currentPlaylist}
                    API={API}
                  />
                </div>
              )}

              {/* Tab: Contratos */}
              {currentTab === 'contratos' && user?.role === 'admin' && (
                <div className="space-y-4">
                  <GestionContratos 
                    playlist={currentPlaylist}
                    API={API}
                  />
                </div>
              )}

              {/* Tab: Apple Watch — ELIMINADO mayo 2026
                  Usuario lleva el reloj pero no usa las funcionalidades implementadas (-629 LoC backend) */}


              {/* Tab: Finanzas - Gestión Financiera del Evento */}
              {currentTab === 'finanzas' && (
                <div className="space-y-4">
                  <EventFinances
                    evento={currentPlaylist}
                    onUpdate={() => {
                      // Refresh playlist data
                      refetchPlaylists();
                    }}
                  />
                </div>
              )}


              {/* Diálogo para añadir peticiones */}
              <div className="mt-6">
                  <Dialog open={isPeticionesDialogOpen} onOpenChange={setIsPeticionesDialogOpen}>
                    <DialogContent className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-xl text-white">Nueva Petición de Canción</DialogTitle>
                        <p className="text-slate-400 text-sm mt-2">
                          Busca en tu biblioteca o en Spotify y añade una nota para esta petición
                        </p>
                      </DialogHeader>
                      
                      <div className="space-y-4">
                        {/* Búsqueda */}
                        <div>
                          <Label className="text-white mb-2">Buscar canción</Label>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Buscar por título o artista..."
                              value={peticionesSearchQuery}
                              onChange={(e) => setPeticionesSearchQuery(e.target.value)}
                              className="bg-slate-700 border-slate-600 text-white"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && peticionesSearchQuery.trim()) {
                                  document.getElementById('peticiones-search-btn').click();
                                }
                              }}
                            />
                            <Button
                              id="peticiones-search-btn"
                              onClick={async () => {
                                if (!peticionesSearchQuery.trim()) {
                                  toast.error('Escribe algo para buscar');
                                  return;
                                }
                                
                                if (!buscarEnBiblioteca && !buscarEnSpotify) {
                                  toast.error('Selecciona al menos una fuente de búsqueda');
                                  return;
                                }
                                
                                setPeticionesSearching(true);
                                try {
                                  let localResults = [];
                                  let spotifyResults = [];
                                  
                                  // Buscar en biblioteca local SOLO si checkbox está marcado
                                  if (buscarEnBiblioteca) {
                                    try {
                                      if (navigator.onLine) {
                                        // Online: usar API del backend
                                        logger.log('[Peticiones] Buscando en biblioteca:', peticionesSearchQuery);
                                        logger.log('[Peticiones] Token disponible:', token ? 'SÍ' : 'NO');
                                        
                                        if (!token) {
                                          console.error('[Peticiones] No hay token de autenticación');
                                          toast.error('Error: No hay sesión activa');
                                          setPeticionesSearching(false);
                                          return;
                                        }
                                        
                                        const response = await axios.get(`${API}/songs`, {
                                          params: {
                                            search: peticionesSearchQuery,
                                            limit: 10
                                          },
                                          headers: { Authorization: `Bearer ${token}` }
                                        });
                                        logger.log('[Peticiones] Respuesta biblioteca:', response.data);
                                        localResults = response.data.data || response.data.songs || [];
                                      } else {
                                        // Offline: buscar en canciones cacheadas
                                        logger.log('[Offline] Buscando en cache local...');
                                        const cachedSongs = await offlineDB.getAll('songs');
                                        const searchLower = peticionesSearchQuery.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                                        
                                        localResults = (cachedSongs || []).filter(song => {
                                          const titleNorm = (song.title || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                                          const artistNorm = (song.artist || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                                          return titleNorm.includes(searchLower) || artistNorm.includes(searchLower);
                                        }).slice(0, 10);
                                        
                                        if (localResults.length === 0) {
                                          toast.info('🔴 Búsqueda offline - Sin resultados en cache');
                                        }
                                      }
                                      logger.log('Resultados de biblioteca local:', localResults.length);
                                    } catch (error) {
                                      console.error('Error buscando en biblioteca:', error);
                                      // Fallback to offline search if API fails
                                      try {
                                        logger.log('[Fallback] API falló, buscando en cache...');
                                        const cachedSongs = await offlineDB.getAll('songs');
                                        const searchLower = peticionesSearchQuery.toLowerCase();
                                        localResults = (cachedSongs || []).filter(song => 
                                          (song.title || '').toLowerCase().includes(searchLower) ||
                                          (song.artist || '').toLowerCase().includes(searchLower)
                                        ).slice(0, 10);
                                      } catch (_cacheError) {
                                        toast.error('Error al buscar en la biblioteca');
                                      }
                                    }
                                  }

                                  // Buscar en Spotify SOLO si checkbox está marcado
                                  if (buscarEnSpotify && accessToken) {
                                    // Spotify requires internet
                                    if (!navigator.onLine) {
                                      toast.warning('🔴 Spotify no disponible offline - Solo búsqueda en biblioteca');
                                    } else {
                                      try {
                                        logger.log('Buscando en Spotify:', peticionesSearchQuery);
                                        logger.log('AccessToken disponible:', accessToken ? 'SÍ' : 'NO');
                                        
                                        // Use cached search if available
                                        const cacheKey = `spotify_${peticionesSearchQuery}`;
                                        const cached = getCachedData(spotifySearchCache, cacheKey);
                                        
                                        if (cached) {
                                          spotifyResults = cached.tracks || [];
                                          logger.log('[CACHE HIT] Spotify tracks:', spotifyResults.length);
                                        } else {
                                          const response = await axios.get(`${API}/spotify/search`, {
                                            params: {
                                              query: peticionesSearchQuery,
                                              access_token: accessToken
                                            }
                                          });
                                          
                                          logger.log('Respuesta de Spotify:', response.data);
                                          spotifyResults = response.data.tracks || [];
                                          
                                          // Cache the results
                                          setCachedData(setSpotifySearchCache, cacheKey, response.data);
                                          logger.log('Tracks encontrados y cacheados:', spotifyResults.length);
                                        }
                                      } catch (spotifyError) {
                                        console.error('❌ Error en búsqueda Spotify:', spotifyError);
                                        console.error('Detalles del error:', spotifyError.response?.data);
                                        toast.error(`Error Spotify: ${spotifyError.response?.data?.detail || spotifyError.message}`);
                                      }
                                    }
                                  } else if (buscarEnSpotify && !accessToken) {
                                    console.warn('Spotify checkbox marcado pero sin accessToken');
                                    toast.warning('Conecta con Spotify para buscar en su catálogo');
                                  }

                                  setPeticionesSearchResults([
                                    ...localResults.map(s => ({ ...s, source: 'biblioteca' })),
                                    ...spotifyResults.slice(0, 5).map(s => ({ ...s, source: 'spotify' }))
                                  ]);
                                  
                                  // Feedback detallado al usuario
                                  if (localResults.length === 0 && spotifyResults.length === 0) {
                                    if (buscarEnBiblioteca && buscarEnSpotify) {
                                      if (!accessToken) {
                                        toast.warning('Sin resultados en biblioteca. Conecta Spotify para más opciones.');
                                      } else {
                                        toast.info('No se encontraron resultados');
                                      }
                                    } else if (buscarEnBiblioteca) {
                                      toast.info('No se encontró en tu biblioteca');
                                    } else {
                                      toast.info('No se encontraron resultados en Spotify');
                                    }
                                  } else {
                                    const bibCount = localResults.length;
                                    const spotCount = spotifyResults.length;
                                    let msg = '';
                                    if (bibCount > 0 && spotCount > 0) {
                                      msg = `${bibCount} en biblioteca, ${spotCount} en Spotify`;
                                    } else if (bibCount > 0) {
                                      msg = `${bibCount} resultados en biblioteca`;
                                    } else {
                                      msg = `${spotCount} resultados en Spotify`;
                                    }
                                    toast.success(msg);
                                  }
                                } catch (error) {
                                  console.error('Error searching:', error);
                                  toast.error(`Error al buscar: ${error.message}`);
                                } finally {
                                  setPeticionesSearching(false);
                                }
                              }}
                              disabled={peticionesSearching}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              {peticionesSearching ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              ) : (
                                <Search className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                          
                          {/* Checkboxes para seleccionar fuentes */}
                          <div className="flex gap-4 mt-3">
                            <label className="flex items-center gap-2 text-white cursor-pointer">
                              <input
                                type="checkbox"
                                checked={buscarEnBiblioteca}
                                onChange={(e) => setBuscarEnBiblioteca(e.target.checked)}
                                className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm">📚 Biblioteca</span>
                            </label>
                            <label className="flex items-center gap-2 text-white cursor-pointer">
                              <input
                                type="checkbox"
                                checked={buscarEnSpotify}
                                onChange={(e) => setBuscarEnSpotify(e.target.checked)}
                                className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-green-600 focus:ring-green-500"
                              />
                              <span className="text-sm">🎵 Spotify</span>
                            </label>
                          </div>
                        </div>

                        {/* Campo de nota */}
                        <div>
                          <Label className="text-white mb-2">Nota (opcional)</Label>
                          <Input
                            placeholder="Ej: Regalo amigos novia"
                            value={peticionNota}
                            onChange={(e) => setPeticionNota(e.target.value)}
                            className="bg-slate-700 border-slate-600 text-white"
                          />
                        </div>

                        {/* Resultados de búsqueda */}
                        {peticionesSearchResults.length > 0 && (
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            <p className="text-slate-400 text-sm">Resultados ({peticionesSearchResults.length})</p>
                            {peticionesSearchResults.map((song, index) => (
                              <div
                                key={song.id || song.spotify_id || song.uri || `${song.title || song.name}-${song.artist}-${index}`}
                                className="bg-slate-900/50 p-3 rounded-lg flex items-center gap-3 hover:bg-slate-900 transition-colors"
                              >
                                <div className="flex-1 min-w-0 overflow-hidden">
                                  <p className="text-white font-medium truncate" title={song.title || song.name}>{song.title || song.name}</p>
                                  <p className="text-slate-400 text-sm truncate">{song.artist || song.artists?.join(', ')}</p>
                                  <span className={`inline-block text-xs px-2 py-1 rounded mt-1 ${
                                    song.source === 'biblioteca' 
                                      ? 'bg-blue-600/20 text-blue-400' 
                                      : 'bg-green-600/20 text-green-400'
                                  }`}>
                                    {song.source === 'biblioteca' ? '📚 Biblioteca' : '🎵 Spotify'}
                                  </span>
                                </div>
                                <Button
                                  onClick={async () => {
                                    try {
                                      const peticion = {
                                        id: `peticion-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                        title: song.title || song.name,
                                        artist: song.artist || song.artists?.join(', '),
                                        nota: peticionNota,
                                        source: song.source,
                                        spotify_url: song.spotify_url || song.external_urls?.spotify,
                                        song_id: song.id, // Guardar ID si viene de biblioteca
                                        added_at: new Date().toISOString(),
                                        added_offline: !navigator.onLine
                                      };

                                      const updatedPeticiones = [...(currentPlaylist.peticiones || []), peticion];
                                      
                                      // Optimistic update - actualizar UI inmediatamente
                                      const updatedPlaylist = {
                                        ...currentPlaylist,
                                        peticiones: updatedPeticiones
                                      };
                                      setCurrentPlaylist(updatedPlaylist);
                                      
                                      // Cerrar diálogo inmediatamente
                                      setIsPeticionesDialogOpen(false);
                                      setPeticionesSearchQuery('');
                                      setPeticionesSearchResults([]);
                                      setPeticionNota('');
                                      toast.success('✅ Petición añadida');
                                      
                                      // Alta atómica ($push) — nunca se envía el array completo
                                      const saveOffline = () => {
                                        offlineDB.save('playlists', updatedPlaylist);
                                        queueOfflineAction('ADD_PETICION', {
                                          playlistId: currentPlaylist.id,
                                          peticion
                                        });
                                      };
                                      if (navigator.onLine) {
                                        axios.post(`${API}/playlists/${currentPlaylist.id}/peticiones`, {
                                          peticion
                                        }).then(() => {
                                          // Sincronizar en segundo plano
                                          refetchPlaylists();
                                        }).catch(apiError => {
                                          console.warn('API failed, saving offline:', apiError);
                                          saveOffline();
                                          toast.warning('🔴 Petición guardada offline - Se sincronizará después');
                                        });
                                      } else {
                                        saveOffline();
                                        toast.warning('🔴 Petición guardada offline');
                                      }
                                    } catch (error) {
                                      console.error('Error adding peticion:', error);
                                      toast.error('Error al añadir petición');
                                    }
                                  }}
                                  size="sm"
                                  className="bg-pink-600 hover:bg-pink-700 flex-shrink-0"
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Dialog: Subir Audio */}
                  {/* Audio Uploader Dialog — extraído a components/AudioUploaderDialog.jsx (Ola E) */}
                  <AudioUploaderDialog
                    isOpen={isAudioUploaderOpen}
                    onOpenChange={setIsAudioUploaderOpen}
                    playlistId={currentPlaylist.id}
                    onUploadSuccess={async () => {
                      await refetchPlaylists();
                      try {
                        const response = await axios.get(`${API}/playlists/${currentPlaylist.id}`, {
                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                        });
                        setCurrentPlaylist(response.data);
                      } catch (error) {
                        console.error('Error reloading playlist:', error);
                      }
                      setIsAudioUploaderOpen(false);
                    }}
                  />

                  {/* Dialog: Importar playlist Spotify a la subpestaña activa */}
                  <SpotifyImportDialog
                    open={spotifyImportOpen}
                    onOpenChange={setSpotifyImportOpen}
                    playlistId={currentPlaylist.id}
                    bucket={activeMomentoBucket}
                    onImported={async () => {
                      try {
                        const response = await axios.get(`${API}/playlists/${currentPlaylist.id}`, {
                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                        });
                        setCurrentPlaylist(response.data);
                      } catch (error) {
                        console.error('Error reloading after Spotify import:', error);
                      }
                    }}
                  />

                  {/* Dialog: Ordenar Lista Personalizada con IA (feb-2026) */}
                  <AIReorderDialog
                    open={aiReorderOpen}
                    onOpenChange={setAiReorderOpen}
                    playlistId={currentPlaylist.id}
                    onApplied={async (orderedIds) => {
                      try {
                        // 1) Reordena inmediatamente los estados locales usando
                        //    los objetos que ya tenemos cacheados (evita el
                        //    problema del useEffect en App.js que sólo refresca
                        //    cuando cambia la LONGITUD, no el orden).
                        if (Array.isArray(orderedIds) && orderedIds.length > 0) {
                          const byId = new Map((generatedCustomList || []).map(s => [s.id, s]));
                          const reordered = orderedIds.map(id => byId.get(id)).filter(Boolean);
                          // Si falta algún objeto (edge case), rehidratamos desde /songs/by-ids
                          if (reordered.length !== orderedIds.length) {
                            try {
                              const r = await axios.post(`${API}/songs/by-ids`, { ids: orderedIds }, {
                                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                              });
                              const byId2 = new Map((r.data.songs || []).map(s => [s.id, s]));
                              const rehydrated = orderedIds.map(id => byId2.get(id)).filter(Boolean);
                              setGeneratedCustomList(rehydrated);
                              setFilteredCustomList(rehydrated);
                            } catch (fetchErr) {
                              console.error('Error rehydrating songs after AI reorder:', fetchErr);
                              setGeneratedCustomList(reordered);
                              setFilteredCustomList(reordered);
                            }
                          } else {
                            setGeneratedCustomList(reordered);
                            setFilteredCustomList(reordered);
                          }
                        }
                        // 2) Refresca el currentPlaylist (por si el backend
                        //    normalizó algún ID o para mantener la fuente de
                        //    verdad sincronizada).
                        const response = await axios.get(`${API}/playlists/${currentPlaylist.id}`, {
                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                        });
                        setCurrentPlaylist(response.data);
                      } catch (error) {
                        console.error('Error reloading after AI reorder:', error);
                      }
                    }}
                  />
              </div>
            </div>
    </>
  );
}

export { PlaylistDetailView };
